import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Rnd } from "react-rnd";
import { CardContext, SlotMachineContext } from "../../../context";
import { useWindowDimensions, useSound, useStrengthCards } from "../../../hooks";
import { ICard, ISlotMachineSpinner, SlotMachineState } from "../../../interfaces";
import { CardSlotMachine } from "../../atoms";
import { checkCardVerticalPositionBeforeAnimating, setTopOfHtmlElementThroughStyle } from "../../molecules/SlotMachine/helpers/index";
import SlotMachineSpinButton from "../../molecules/SlotMachine/SlotMachineSpinButton";
import useStrengthCardsDemo from "./useStrengthCardsDemo";

const SlotMachineSpinner = (props: ISlotMachineSpinner) => {
    const { slotMachineRef } = props;

    const { setDraggableCard, setIsStrengthCardCurrentlyDragged } = useContext(CardContext);
    const { 
        isConfirmSelectionClicked, 
        verticalCardIndexOfSelectedRow, 
        setVerticalCardIdsAndTheirIndexes   
    } = useContext(SlotMachineContext);

    const [draggingCardIndex, setDraggingCardIndex] = useState<number>(-1);
    const [isChoosingCardsLocked, setIsChoosingCardsLocked] = useState<boolean>(false);
    const lockCardTimeout = useRef<NodeJS.Timeout>();
    let [recievedStrengthCards] = useStrengthCards();
    
    const [shuffledRefs, setShuffledRefs] = useState(
        [useRef<HTMLDivElement | null>(null),
        useRef<HTMLDivElement | null>(null),
        useRef<HTMLDivElement | null>(null),
        useRef<HTMLDivElement | null>(null),
        useRef<HTMLDivElement | null>(null)]
    );
    const [strengthCards, setStrengthCards] = useState<SlotMachineState>({
        card1: { cardData: undefined },
        card2: { cardData: undefined },
        card3: { cardData: undefined },
        card4: { cardData: undefined },
        card5: { cardData: undefined }
    });

    const { width } = useWindowDimensions();
    const {playSound} = useSound();

    useEffect(() => {
        if (recievedStrengthCards.length)
            playSound("/sounds/slotSpin.mp3");
    }, [playSound, recievedStrengthCards])

    
    useEffect(() => {
        if (recievedStrengthCards.length && !(draggingCardIndex+1) ) {
            shuffleRecievedCards();
            spinAllCards();
        } 
        // if I add "draggingCardIndex" all the cards will shuffle instead of suffling only one (the selected one)
        // shuffleRecievedCards - has to be transform into callback, and then it will need its dependancies
        // spinAllCards - has to be transform into callback, and then it will need its dependancies
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recievedStrengthCards]);
        
    useEffect(() => {
        if (recievedStrengthCards.length && (draggingCardIndex+1) )
            spinOnlyOneCard();
        // if I add "draggingCardIndex" all the cards will shuffle instead of suffling only one (the selected one)
        // spinOnlyOneCard - has to be transform into callback, and then it will need its dependancies
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recievedStrengthCards]);

    useEffect(() => {
        return () => {
            if(lockCardTimeout.current)
                clearTimeout(lockCardTimeout.current);
        }
    }, []);

    const shuffleRecievedCards = () => {
        const newArray = shuffledRefs;

        for (let i = newArray.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
        }

        setShuffledRefs(newArray);
    };

    const spinAllCards = () => {
        const slotState = Object.assign({}, strengthCards);

        shuffledRefs.forEach((ref, index) => {
            if (!ref.current) return;
            const selected = triggerSlotRotation(ref.current, index, false);
            slotState[`card${index + 1}`].cardData = selected;
        });

        setStrengthCards(slotState);
    };

    const spinOnlyOneCard = () => {
        const refToShuffle = shuffledRefs[draggingCardIndex].current;

        if(!refToShuffle) return ;

        const selected = triggerSlotRotation(refToShuffle, draggingCardIndex, true);

        setStrengthCards({
            ...strengthCards,
            strengthCards: {
                [`card${draggingCardIndex + 1}`]: selected
            } 
        });

        setDraggingCardIndex(-1);
    }

    const lockOtherCardsWhileOneCardIsSpinning = () => {
        lockCardTimeout.current = setTimeout(() => {
            setIsChoosingCardsLocked(false);
        }, 600);
        setIsChoosingCardsLocked(true);
    }

    const triggerSlotRotation = (element: HTMLDivElement, index: number, isSpinningOnlyOne: boolean) => {
        const options = element.children;
        if (isSpinningOnlyOne) {
            checkCardVerticalPositionBeforeAnimating(element, options, isSpinningOnlyOne, verticalCardIndexOfSelectedRow);
            lockOtherCardsWhileOneCardIsSpinning();
        }

        if (!isSpinningOnlyOne) {
            const choosenOption = options[index] as HTMLDivElement;
            setTopOfHtmlElementThroughStyle(element, -choosenOption.offsetTop, isSpinningOnlyOne);
        }
        
        return recievedStrengthCards[index];
    };

    const keepCard = useCallback((cardData: ICard) => {
        setDraggableCard(cardData);
        if (isConfirmSelectionClicked) return;
    }, [setDraggableCard, isConfirmSelectionClicked]);

    const renderRecievedCards = useMemo (() => {
        if (!recievedStrengthCards.length) return null;
        
        let cardAndVerticalIds: any[] = [];

        return (
            recievedStrengthCards.map((card: ICard, verticalIndex: number) => {

                cardAndVerticalIds.push({cardId: card.id || 0, verticalIndex: verticalIndex})
                setVerticalCardIdsAndTheirIndexes(cardAndVerticalIds);

                return(
                <div key={verticalIndex} style={{margin: "30px 0"}}>
                    <CardSlotMachine key={card.id} cardData={card} onClick={() => keepCard(card)} />
                </div> 
            )})
        );
    },[recievedStrengthCards, setVerticalCardIdsAndTheirIndexes, keepCard]);

    return(
        <div className="slot_machine">
            <div className="slot_machine_cards_wrapper blue_inset_shadow" style={{ height: width * 0.18}}> 
                {Array.apply(null, Array(5)).map( (_item: any, index: number) => {
                    return(
                        <div className="slot_machine__card_slots" style={{ height: width * 0.157, width: width * 0.119 }} key={index}>
                            <Rnd
                                style={{ cursor: 'pointer' }}
                                enableResizing={false}
                                position={{ x: 0, y: 0 }}
                                disableDragging={isChoosingCardsLocked}
                                onDragStart={() => {
                                    setIsStrengthCardCurrentlyDragged(true);
                                    setDraggingCardIndex(index);
                                    
                                }}
                                onDragStop={ () => {
                                    slotMachineRef.current?.dropedStrenghtCard();
                                }}
                            >
                                <section style={{ height: width * 0.156, width: width * 0.12 }} >
                                    <div className="slot__wrapper" style={{ height: width * 0.157, width: width * 0.119 }} ref={(el) => shuffledRefs[index].current = el}>
                                        {renderRecievedCards}
                                    </div>
                                </section>
                            </Rnd>
                            <div className="slot_machine_placeholder_image_wrapper center_horizontally_and_vertically">
                                <div className="slot_machine_placeholder_image"></div>
                            </div>
                            {index !== 4 && <div className="linear_gradient_line_vertical"></div>}
                        </div>
                    );
                }) }
            </div>
            <div className="slot_machine_lever_image_wrapper"  style={{ height: width * 0.18}}> 
                <SlotMachineSpinButton isInDemo={true} />
            </div>
        </div>
    )
}

export default SlotMachineSpinner;